import { cid24, DrugScannerResult } from '@fusion-advantage/formlib'
import { useCallback, useEffect, useRef } from 'react'
import { showBarcodeScannerArgs, showRxScannerArgs, NativePluginEventName } from '@fusion-advantage/backcompat'
import { useFormAppRelay } from './relay/FormAppRelay'
import { useFormDispatch, useFormSelector } from '../redux/formStoreHooks'
import { setPluginListenerId } from '../redux/formSlice'

interface useBarcodeScannerPluginProps {
	onReceive: (code: string) => void
}

export const useBarcodeScannerPlugin = ({onReceive}: useBarcodeScannerPluginProps) => {
	const instanceId = useRef<string>(cid24('i'))
	const listenerInstanceId = useFormSelector(state => state.form.pluginListenerId[NativePluginEventName.ReceiveBarcode])
	const dispatch = useFormDispatch()
	const { showBarcodeScanner: relayShow, addNativePluginListener } = useFormAppRelay()

	const showBarcodeScanner = useCallback((args: showBarcodeScannerArgs) => {
		dispatch(setPluginListenerId({
			name: NativePluginEventName.ReceiveBarcode,
			id: instanceId.current,
		}))
		relayShow(args)
	}, [dispatch, relayShow])

	const handleReceiveBarcode = useCallback((data: any) => {
		if(instanceId.current !== listenerInstanceId) {
			return
		}
		const code: string = data.results
		console.info(`[useBarcodeScannerPlugin ${instanceId.current} handleReceiveBarcode]`, code)
		onReceive(code)
	}, [listenerInstanceId, onReceive])

	useEffect(() => {
		const { remover } = addNativePluginListener({
			name: NativePluginEventName.ReceiveBarcode,
			handler: handleReceiveBarcode,
		})
		return remover
	}, [addNativePluginListener, handleReceiveBarcode])

	return {
		showBarcodeScanner,
	}
}

interface useRxScannerPluginProps {
	onReceive: (results: DrugScannerResult[]) => void
}

export const useRxScannerPlugin = ({onReceive}: useRxScannerPluginProps) => {
	const instanceId = useRef<string>(cid24('i'))
	const listenerInstanceId = useFormSelector(state => state.form.pluginListenerId[NativePluginEventName.ReceiveSelectedProducts])
	const dispatch = useFormDispatch()
	const { showRxScanner: relayShow, addNativePluginListener } = useFormAppRelay()

	const showRxScanner = useCallback((args: showRxScannerArgs) => {
		dispatch(setPluginListenerId({
			name: NativePluginEventName.ReceiveSelectedProducts,
			id: instanceId.current,
		}))
		relayShow(args)
	}, [dispatch, relayShow])

	const handleReceiveProducts = useCallback((data: any) => {
		if(instanceId.current !== listenerInstanceId) {
			return
		}
		const products: DrugScannerResult[] = data.results
		console.info(`[useRxScannerPlugin ${instanceId.current} handleReceiveProducts]`, products)
		onReceive(products)
	}, [listenerInstanceId, onReceive])

	useEffect(() => {
		const { remover } = addNativePluginListener({
			name: NativePluginEventName.ReceiveSelectedProducts,
			handler: handleReceiveProducts,
		})
		return remover
	}, [addNativePluginListener, handleReceiveProducts])

	return {
		showRxScanner,
	}
}
